import React, { useEffect, useState } from 'react'
import api from '../services/api'
import { Card } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material'
import { toast } from 'react-toastify'
import { useAuth } from '../context/AuthContext'

const MODEL_OPTIONS = [
  { value: 'openai:gpt-4o-mini', label: 'GPT-4o mini (OpenAI)' },
  { value: 'openai:gpt-4.1', label: 'GPT-4.1 (OpenAI)' },
  { value: 'anthropic:claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
  { value: 'google:gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
  { value: 'google:gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  { value: 'google:gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (preview)' },
  { value: 'google:gemini-3-flash', label: 'Gemini 3 Flash (preview)' },
  { value: 'google:gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash-Lite' },
  { value: 'google:gemini-3-flash-preview', label: 'Gemini 3 Flash (preview) (legacy id)' }
]

function getModelLabel(value) {
  if (!value) return 'non impostato'
  const found = MODEL_OPTIONS.find((m) => m.value === value)
  return found ? found.label : value
}

function getProviderLabelFromModel(value) {
  if (!value) return 'sconosciuto'
  const [provider] = value.split(':')
  if (provider === 'openai') return 'OpenAI'
  if (provider === 'anthropic') return 'Anthropic (Claude)'
  if (provider === 'google') return 'Google (Gemini)'
  return provider || 'sconosciuto'
}

function AiSettingsAdmin() {
  const { user } = useAuth()
  const roleName = (user?.role_name || user?.role || '').toLowerCase()
  const isAdmin = roleName === 'admin'

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [defaultModel, setDefaultModel] = useState('')
  const [hasOpenaiKey, setHasOpenaiKey] = useState(false)
  const [hasAnthropicKey, setHasAnthropicKey] = useState(false)
  const [hasGoogleKey, setHasGoogleKey] = useState(false)
  const [openaiHint, setOpenaiHint] = useState('')
  const [anthropicHint, setAnthropicHint] = useState('')
  const [googleHint, setGoogleHint] = useState('')
  const [openaiSource, setOpenaiSource] = useState('')
  const [anthropicSource, setAnthropicSource] = useState('')
  const [googleSource, setGoogleSource] = useState('')
  const [testingProvider, setTestingProvider] = useState('')
  const [openaiKey, setOpenaiKey] = useState('')
  const [anthropicKey, setAnthropicKey] = useState('')
  const [googleKey, setGoogleKey] = useState('')

  useEffect(() => {
    if (!isAdmin) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await api.get('/ai/settings')
        const data = res.data || {}
        setDefaultModel(data.defaultModel || '')
        const providers = data.providers || {}
        setHasOpenaiKey(!!providers.openai?.hasKey)
        setOpenaiHint(providers.openai?.hint || '')
        setOpenaiSource(providers.openai?.source || '')
        setHasAnthropicKey(!!providers.anthropic?.hasKey)
        setAnthropicHint(providers.anthropic?.hint || '')
        setAnthropicSource(providers.anthropic?.source || '')
        setHasGoogleKey(!!providers.google?.hasKey)
        setGoogleHint(providers.google?.hint || '')
        setGoogleSource(providers.google?.source || '')
      } catch (e) {
        toast.error('Errore nel caricamento impostazioni AI')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isAdmin])

  const handleSave = async () => {
    try {
      setSaving(true)
      const payload = {
        defaultModel: defaultModel || undefined,
        openaiApiKey: openaiKey || undefined,
        anthropicApiKey: anthropicKey || undefined,
        googleApiKey: googleKey || undefined
      }
      const res = await api.post('/ai/settings', payload)
      const data = res.data || {}
      setDefaultModel(data.defaultModel || '')
      const providers = data.providers || {}
      setHasOpenaiKey(!!providers.openai?.hasKey)
      setOpenaiHint(providers.openai?.hint || '')
      setOpenaiSource(providers.openai?.source || '')
      setHasAnthropicKey(!!providers.anthropic?.hasKey)
      setAnthropicHint(providers.anthropic?.hint || '')
      setAnthropicSource(providers.anthropic?.source || '')
      setHasGoogleKey(!!providers.google?.hasKey)
      setGoogleHint(providers.google?.hint || '')
      setGoogleSource(providers.google?.source || '')
      setOpenaiKey('')
      setAnthropicKey('')
      setGoogleKey('')
      toast.success('Impostazioni AI salvate')
    } catch (e) {
      const msg = e?.response?.data?.message || 'Errore nel salvataggio impostazioni AI'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleTestProvider = async (provider) => {
    try {
      setTestingProvider(provider)
      const res = await api.post('/ai/settings/test', { provider })
      const data = res.data || {}
      if (!data.hasKey) {
        toast.error(data.error || `Nessuna chiave configurata per ${provider}`)
        return
      }
      if (data.ok) {
        const hint = data.hint || ''
        const src = data.source === 'env' ? 'env' : 'settings'
        toast.success(`Chiave ${provider} valida (${src} ${hint})`)
      } else {
        const raw = data.error || ''
        let message = `Errore nel test della chiave per ${provider}`
        const statusMatch = raw.startsWith('Status ') ? raw.slice(7) : null
        if (statusMatch) {
          message = `Chiave ${provider} non valida o mancante (codice ${statusMatch})`
        } else if (raw) {
          message = `${message}: ${raw}`
        }
        toast.error(message)
      }
    } catch (e) {
      const msg = e?.response?.data?.message || 'Errore nel test della chiave'
      toast.error(msg)
    } finally {
      setTestingProvider('')
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold mb-2">Impostazioni AI</h1>
        <p className="text-sm text-muted-foreground">Accesso riservato agli amministratori.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Impostazioni AI</h1>
          {defaultModel && (
            <p className="text-xs text-muted-foreground mt-1">
              Default: {getModelLabel(defaultModel)} (provider: {getProviderLabelFromModel(defaultModel)})
            </p>
          )}
        </div>
      </div>

      <Card className="p-4 space-y-4">
        <div className="space-y-2">
          <h2 className="text-sm font-semibold">Modello di default</h2>
          <p className="text-xs text-muted-foreground">
            Modello usato quando una conversazione non ha un modello specifico impostato.
          </p>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="ai-default-model-label">Modello di default</InputLabel>
            <Select
              labelId="ai-default-model-label"
              label="Modello di default"
              value={defaultModel}
              onChange={(e) => setDefaultModel(e.target.value)}
            >
              {MODEL_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <h2 className="text-sm font-semibold">Chiavi API provider</h2>
        <p className="text-xs text-muted-foreground">
          Le chiavi vengono salvate in backend e non sono mai restituite in chiaro. Inserisci una nuova chiave solo
          quando vuoi aggiornarla; i campi vuoti non modificano il valore esistente.
        </p>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-32 text-xs font-medium">OpenAI</div>
            <Badge variant={hasOpenaiKey ? 'outline' : 'secondary'} className="text-[10px]">
              {hasOpenaiKey ? 'Chiave configurata' : 'Chiave non impostata'}
            </Badge>
            {hasOpenaiKey && openaiHint && (
              <span className="text-[10px] text-muted-foreground">
                ({openaiSource === 'env' ? 'env ' : ''}{openaiHint})
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleTestProvider('openai')}
              disabled={testingProvider === 'openai'}
            >
              {testingProvider === 'openai' ? 'Test...' : 'Test'}
            </Button>
            <Input
              type="password"
              placeholder="Nuova chiave OpenAI..."
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
              className="flex-1 min-w-[200px]"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="w-32 text-xs font-medium">Anthropic (Claude)</div>
            <Badge variant={hasAnthropicKey ? 'outline' : 'secondary'} className="text-[10px]">
              {hasAnthropicKey ? 'Chiave configurata' : 'Chiave non impostata'}
            </Badge>
            {hasAnthropicKey && anthropicHint && (
              <span className="text-[10px] text-muted-foreground">
                ({anthropicSource === 'env' ? 'env ' : ''}{anthropicHint})
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleTestProvider('anthropic')}
              disabled={testingProvider === 'anthropic'}
            >
              {testingProvider === 'anthropic' ? 'Test...' : 'Test'}
            </Button>
            <Input
              type="password"
              placeholder="Nuova chiave Anthropic..."
              value={anthropicKey}
              onChange={(e) => setAnthropicKey(e.target.value)}
              className="flex-1 min-w-[200px]"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="w-32 text-xs font-medium">Google Gemini</div>
            <Badge variant={hasGoogleKey ? 'outline' : 'secondary'} className="text-[10px]">
              {hasGoogleKey ? 'Chiave configurata' : 'Chiave non impostata'}
            </Badge>
            {hasGoogleKey && googleHint && (
              <span className="text-[10px] text-muted-foreground">
                ({googleSource === 'env' ? 'env ' : ''}{googleHint})
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleTestProvider('google')}
              disabled={testingProvider === 'google'}
            >
              {testingProvider === 'google' ? 'Test...' : 'Test'}
            </Button>
            <Input
              type="password"
              placeholder="Nuova chiave Gemini..."
              value={googleKey}
              onChange={(e) => setGoogleKey(e.target.value)}
              className="flex-1 min-w-[200px]"
            />
          </div>
        </div>

        <div className="pt-2">
          <Button size="sm" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Salvataggio...' : 'Salva impostazioni'}
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default AiSettingsAdmin
